import {
  API_DATE_FORMAT,
  LeaveRequestStatus,
  UnavailabilityRecordType,
  UnavailabilityStatus,
} from '$constants'
import { ILeaveRequest, ILeaveType, IUnavailabilityRecord } from '$models'
import {
  leaveRequestService,
  leaveTypeService,
  unavailabilityService,
} from '$services'
import { Dayjs } from 'dayjs'
import { StateCreator } from 'zustand'

interface WorkforceSharedState {
  leaveRequests: ILeaveRequest[]
  leaveTypes: ILeaveType[]
  unavailabilityRecords: IUnavailabilityRecord[]
}

export interface WorkforceSharedSlice extends WorkforceSharedState {
  /**
   * Reset WorkforceShared Slice
   */
  resetSharedWorkforceSlice: () => void

  /**
   * Get all leave requests, filtered by the parameters
   * @param staffMemberIds [optional] staff member ids filter
   * @param from [optional] from date filter to get leave requests after or at this date (eg. "2025-02-20")
   * @param to [optional] to date filter to get leave requests before this date (e.g. "2025-02-20")
   * @param statuses [optional] filter to only fetch leave requests of a certain status
   * @param requestedFrom [optional] from date filter to get leave requested at or after this date
   * @param requestTo [optional] to date filter to get leave requested before this date
   * @returns All leave requests that pass the given filter params
   */
  getLeaveRequests: (
    staffMemberIds?: string[],
    from?: Dayjs,
    to?: Dayjs,
    statuses?: LeaveRequestStatus[],
    requestedFrom?: Dayjs,
    requestTo?: Dayjs
  ) => Promise<ILeaveRequest[]>

  /**
   * Get all leave types
   * @returns all leave types for the company
   */
  getLeaveTypes: () => Promise<ILeaveType[]>

  /**
   * Get all unavailability records, filtered by the parameters
   * @param staffMemberId [optional] staff member ids filter
   * @param from [optional] from date filter to get unavailability records after or at this date (eg. "2025-02-20")
   * @param to [optional] to date filter to get unavailability records before this date (e.g. "2025-02-20")
   * @param type [optional] types of unavailability to fetch
   * @param statuses [optional] filter to only fetch leave requests of a certain status
   * @param requestedFrom [optional] from date filter to get unavailability records requested at or after this date
   * @param requestTo [optional] to date filter to get unavailability records requested before this date
   * @returns
   */
  getUnavailability: (
    staffMemberId?: string,
    from?: string,
    to?: string,
    type?: UnavailabilityRecordType,
    statuses?: UnavailabilityStatus[],
    requestedFrom?: string,
    requestedTo?: string
  ) => Promise<IUnavailabilityRecord[]>
}

const initialState: WorkforceSharedState = {
  leaveRequests: [],
  leaveTypes: [],
  unavailabilityRecords: [],
}

const createSharedWorkforceSlice: StateCreator<
  WorkforceSharedSlice,
  [],
  [],
  WorkforceSharedSlice
> = (set) => ({
  ...initialState,

  resetSharedWorkforceSlice: () => {
    set(initialState)
  },

  getLeaveRequests: async (
    staffMemberIds?: string[],
    from?: Dayjs,
    to?: Dayjs,
    statuses?: LeaveRequestStatus[],
    requestedFrom?: Dayjs,
    requestTo?: Dayjs
  ): Promise<ILeaveRequest[]> => {
    const fromDate = from?.format(API_DATE_FORMAT)
    const toDate = to?.format(API_DATE_FORMAT)
    const requestedFromDate = requestedFrom?.format(API_DATE_FORMAT)
    const requestedToDate = requestTo?.format(API_DATE_FORMAT)

    const leaveRequests = await leaveRequestService.getAll(
      staffMemberIds,
      fromDate,
      toDate,
      statuses,
      requestedFromDate,
      requestedToDate
    )

    set(() => ({
      leaveRequests: leaveRequests,
    }))

    return leaveRequests
  },

  getLeaveTypes: async () => {
    const leaveTypes = await leaveTypeService.getAll()

    set(() => ({
      leaveTypes: leaveTypes,
    }))

    return leaveTypes
  },

  getUnavailability: async (
    staffMemberId?: string,
    from?: string,
    to?: string,
    type?: UnavailabilityRecordType,
    statuses?: UnavailabilityStatus[],
    requestedFrom?: string,
    requestedTo?: string
  ) => {
    const unavailability = await unavailabilityService.getAll(
      staffMemberId,
      from,
      to,
      type,
      statuses,
      requestedFrom,
      requestedTo
    )

    set(() => ({
      unavailabilityRecords: unavailability,
    }))

    return unavailability
  },
})

export default createSharedWorkforceSlice
